import { createTheme, type MantineColorsTuple } from "@mantine/core";

/**
 * The single source of design tokens for both applications (arch §4.2).
 *
 * Colour, spacing and typography come from here so they cannot drift between the customer app
 * and the host panel. SCSS modules handle layout and anything component-specific; they do not
 * redefine tokens.
 */

/**
 * Peeko yellow. Shade 6 is the brand colour exactly — `#ffd100` — and the rest of the ramp holds
 * its hue while moving lightness, so a hover or a subtle fill still reads as the brand.
 *
 * **Yellow is a fill, never text.** This is not a stylistic preference, it is arithmetic: at
 * `1.9:1` against white the brand yellow fails WCAG AA for text by a wide margin, and even the
 * darkest rung of this ramp only reaches `4.1:1`. Anything that has to be *read* uses `ink`.
 */
const peeko: MantineColorsTuple = [
  "#fffae5",
  "#fff4c2",
  "#ffec99",
  "#ffe570",
  "#ffdd47",
  "#ffd724",
  "#ffd100",
  "#e6b800",
  "#c29800",
  "#9e7900",
];

/**
 * Peeko ink, the brand's contrast colour. Shade 8 is `#052121` exactly.
 *
 * A very dark desaturated teal rather than a neutral grey, which is what keeps text and borders
 * looking related to the brand instead of merely dark. `#052121` on white is `16.9:1`.
 */
const ink: MantineColorsTuple = [
  "#f3f7f7",
  "#e1eaea",
  "#bfcfcf",
  "#97afaf",
  "#6e9191",
  "#507171",
  "#365959",
  "#194343",
  "#052121",
  "#041b1b",
];

/**
 * Peeko flame — the brand's red.
 *
 * Shade 6 is `#d1384e` exactly, sampled from the designs rather than chosen: it is the fill of the
 * `LIVE` pill and of the alert band, and those are the only places in the room that are red.
 *
 * **Unlike the yellow, this one is legible both ways round**, which is what lets it carry an alert.
 * White on shade 6 is `4.79:1` — clear of WCAG AA for normal text, with nothing to spare, so a
 * lighter shade must never be substituted under white. Shade 6 on the room's near-black stage is
 * `4.03:1`, which carries the countdown and the `Sold` mark: both are large text, where the
 * threshold is 3:1.
 *
 * The designs use a second, warmer red (`#df483a`) for the countdown and `Sold` while reserving
 * `#d1384e` for the pill and the band. That distinction is deliberately **not** reproduced:
 * eighteen degrees of hue between two reds that never share a screen is a difference nobody can
 * use, and one red is one fewer thing to keep in step.
 */
const flame: MantineColorsTuple = [
  "#f8f1f2",
  "#efdcde",
  "#e5bdc3",
  "#db99a2",
  "#d47280",
  "#d15264",
  "#d1384e",
  "#b1293c",
  "#8e2131",
  "#701a26",
];

export const theme = createTheme({
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  headings: { fontWeight: "650" },
  defaultRadius: "md",

  /**
   * `blue` and `green` are Mantine's own and are deliberately not replaced.
   *
   * The designs carry an informational blue (`#6d9ff3`) and a success green (`#80bf88`), and both
   * were measured before being adopted: as *text on white* they reach `2.66:1` and `2.16:1`, which
   * fails WCAG AA by a wide margin. Both were used in the comps on labels this build does not ship
   * (`BEST SELLER`, `Next Day Delivery`), so nothing is lost by taking Mantine's ramps instead —
   * and a framework ramp that passes is worth more than a brand ramp that does not.
   *
   * Where either is used as **text on a light surface**, use shade 8 or darker.
   */
  colors: { peeko, ink, flame },
  primaryColor: "peeko",
  primaryShade: 6,

  /**
   * **This pair is load-bearing, and getting it wrong makes every primary button unreadable.**
   *
   * Mantine's default is white text on the primary colour. White on `#ffd100` is `1.46:1` — not
   * "a bit low", but genuinely illegible, and the brand colour is light enough that this is the
   * expected outcome rather than bad luck.
   *
   * `autoContrast` makes Mantine choose between `theme.black` and `theme.white` per swatch by
   * luminance, and `black` is overridden below to the brand's own `#052121`. So a yellow button
   * gets brand ink on brand yellow at `11.5:1`, automatically, everywhere — including in
   * components nobody has written yet, which is the reason to fix it here rather than in a
   * `styles` prop per button.
   */
  autoContrast: true,
  luminanceThreshold: 0.3,

  /** Brand ink rather than `#000`, so "black" text anywhere in either app is the brand's black. */
  black: "#052121",
  white: "#ffffff",
});
